import type { LDFlagChangeset, LDUser } from "launchdarkly-js-client-sdk";
import initLdClient from "launchdarkly-react-client-sdk/lib/initLDClient";
import type { AllFlagsLDClient } from "launchdarkly-react-client-sdk/lib/types";
import { eventChannel } from "redux-saga";
import { call, put, select, take, cancelled, takeLatest } from "redux-saga/effects";
import axios from "axios";

import { AUTH_USER, INIT_VISITOR_ID, UNAUTH_USER } from "../../actions/types";
import type { RootState } from "../rootReducer";

import { flagsUpdated, loaded } from "./reducer";

function* updateFeatureFlags() {
  try {
    const flagsStatus = ((yield select()) as RootState).featureFlags;
    if (flagsStatus.flags) {
      yield call(axios.post, "/feature_flags", {
        feature_flags: flagsStatus.flags,
      });
    }
  } catch (err) {
    console.log(err);
  }
}

function* connectLDToRedux(client: AllFlagsLDClient) {
  const flagChangeWatcher = eventChannel((emit) => {
    // We are creating this channel in order to connect external event source,
    // in this case LaunchDarkly client to the saga that will process these
    // events and send them on to redux

    const errorHandler = (errorEvent: Error) => {
      // LaunchDarkly has a separate event handler for errors. In our world we
      // emit errors as another event on the channel

      // We used to create a new Error and pass 'reason' prop from 'errorEvent' but
      // apparently that was never set so we'd up reporting very generic and useless
      // Sentry alerts. Based on LD docs, seems 'errorEvent' is already an Error
      // instance, so let's see what happens if we just pass that along
      emit(errorEvent);
    };
    const changeHandler = (changeEvent: LDFlagChangeset) => {
      emit(flagsUpdated(changeEvent));
    };

    client.ldClient.on("change", changeHandler); // Listen
    client.ldClient.on("error", errorHandler); // Listen
    return () => {
      // This function is called on occuring error or `break;`
      client.ldClient.off("change", changeHandler); // UnListen
      client.ldClient.off("error", errorHandler); // UnListen
    };
  });
  try {
    yield put(loaded(client.flags as any));
    yield call(updateFeatureFlags);

    // Handle updating flags when they change
    while (true) {
      const action = yield take(flagChangeWatcher);
      yield put(action);
      yield call(updateFeatureFlags);
    }
  } finally {
    if (yield cancelled()) {
      flagChangeWatcher.close();
    }
  }
}

function* setupLaunchDarkly() {
  // Handle `.identify()`ing launchDarkly when user changes
  yield takeLatest([AUTH_USER, UNAUTH_USER, INIT_VISITOR_ID], function* () {
    const user = ((yield select()) as RootState).auth;

    let identifyArgs: LDUser;

    if (user.user_id) {
      identifyArgs = {
        key: user.user_id,
        email: user.email,
        name: user.full_name,
        anonymous: false,
        custom: {
          role: user.role,
          visitorId: user.visitorId,
          orgCode: user.organization_info?.organizational_code,
          orgName: user.organization_info?.organization_name,
        },
      };
    } else {
      identifyArgs = { anonymous: true, key: "anonymous_untracked_user" };
    }

    const client: AllFlagsLDClient = yield initLdClient(process.env.LAUNCH_DARKLY_ID, identifyArgs, {
      autoAliasingOptOut: true, // Stop sending alias events to re-alias "anonymous_untracked_user" to everybody
    });

    yield call(connectLDToRedux, client);
  });
}

export function* featureFlagSagas() {
  yield call(setupLaunchDarkly);
}
